// The station sheet's health section (F-STN-11, F-LCK-08, spec/13 §3.3): whether the PC is healthy, the alerts that
// are open (critical first as the station raised them), the recent ones that cleared, and its latest reading — CPU,
// memory, free disk and GPU temperature — with free disk over the last day as a small trend line.
//
// How an alert goes (2026-10-02): each open alert says how old it is and how it clears by itself — "clears once the PC
// has 11 GB free again", or, for a one-off event, "at the latest 14:05 tomorrow if it does not happen again". A member
// with `stations:alerts_clear` may clear one (or all) by hand with a note, or snooze them; a warning the PC still reports
// comes back. The cleared ones below say how they went: fixed on the PC, expired, or cleared by staff with their note.

import type { FloorStationState, StationAlert, StationPolicyStatus, StationTelemetrySample } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, cx } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { Icon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { Section } from '../ui';
import { formatAgo } from './ago';
import { ClearAlertsForm } from './clear-alerts';
import { useNow } from './live';
import { useStationHealth, useStationTelemetry } from './queries';
import { alertWords } from './tile';
import { useProblem } from '../api/use-problem';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const when = (iso: string): string => new Date(iso).toLocaleString();

/** How an open alert clears by itself, in words (F-STN-11, 2026-10-02). */
function clearRuleWords(alert: StationAlert, t: Translate): string {
  if (alert.clearRule === 'quiet_period') {
    return alert.expiresAt === undefined ? t('floor.drawer.health.quietPeriod') : t('floor.drawer.health.expiresAt', { time: when(alert.expiresAt) });
  }
  return t(`floor.drawer.health.clearsWhen.${alert.kind}`, { defaultValue: t('floor.drawer.health.clearsWhen.unknown') });
}

/** How a cleared alert went: fixed on the PC, expired, or cleared by staff. */
function clearedWords(alert: StationAlert, t: Translate): string {
  return t(`floor.drawer.health.clearReason.${alert.clearReason ?? 'resolved'}`, { time: when(alert.clearedAt ?? alert.openedAt) });
}

/**
 * What the PC itself reported, word for word, folded away (2026-10-07). It is technical — a Windows error, a policy
 * read-back — and is there for support; the alert's title and its plain explanation are what staff read
 * (spec/BRAND.md §6.2).
 */
function TechnicalDetails({ detail }: { readonly detail: string }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <details className="console-health__technical">
      <summary>{t('floor.drawer.health.technicalDetails')}</summary>
      <p className="console-muted">{t('floor.drawer.health.technicalDetailsHint')}</p>
      <code>{detail}</code>
    </details>
  );
}

function AlertRow({
  alert,
  now,
  onClear,
}: {
  readonly alert: StationAlert;
  readonly now: number;
  /** Offered to a member who may clear it; absent otherwise, and never on a cleared alert. */
  readonly onClear?: (() => void) | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  const cleared = alert.clearedAt !== null;
  const snoozed = !cleared && alert.snoozedUntil !== undefined;
  const words = alertWords(alert.kind, t);
  return (
    <li
      className={cx(
        'console-health__alert',
        !cleared && !snoozed && alert.severity === 'critical' && 'console-health__alert--critical',
        (cleared || snoozed) && 'console-health__alert--cleared',
      )}
    >
      <Icon name={cleared ? 'check' : 'alert'} />
      <span className="console-health__alert-body">
        <strong>{words}</strong>
        {snoozed ? (
          <>
            {' '}
            <Badge tone="neutral">{t('floor.drawer.health.snoozedBadge')}</Badge>
          </>
        ) : null}
        <br />
        <span>{t(`floor.drawer.health.explain.${alert.kind}`, { defaultValue: t('floor.drawer.health.explain.unknown') })}</span>
        <br />
        <span className="console-muted">
          {cleared
            ? clearedWords(alert, t)
            : `${t('floor.drawer.health.age', { age: formatAgo(alert.openedAt, t, now) })} · ${t('floor.drawer.health.openedAt', { time: when(alert.openedAt) })}`}
        </span>
        {cleared ? null : (
          <>
            <br />
            <span className="console-muted">
              {snoozed && alert.snoozedUntil !== undefined ? `${t('floor.drawer.health.snoozedUntil', { time: when(alert.snoozedUntil) })} · ` : ''}
              {clearRuleWords(alert, t)}
            </span>
          </>
        )}
        {alert.clearNote === undefined ? null : (
          <>
            <br />
            <span className="console-muted">{t('floor.drawer.health.note', { note: alert.clearNote })}</span>
          </>
        )}
        {alert.detail === null ? null : <TechnicalDetails detail={alert.detail} />}
      </span>
      {onClear === undefined || cleared ? null : (
        <Button variant="ghost" size="sm" className="console-health__alert-clear" onClick={onClear} aria-label={t('floor.drawer.health.clearOne', { alert: words })}>
          {t('floor.drawer.health.clear')}
        </Button>
      )}
    </li>
  );
}

/**
 * Whether the PC runs the venue's current settings (F-STN-09, spec/13 §3.3): the policy version it last reported
 * against the one the server builds for it now. A settings change shows "pending" until the PC applies it — seconds,
 * while it is online.
 */
function SettingsState({ policy }: { readonly policy: StationPolicyStatus | undefined }): ReactNode {
  const { t } = useTranslation('console');
  if (policy === undefined) return null;
  const [tone, label, hint] =
    policy.upToDate === true
      ? (['ok', 'upToDate', 'upToDateHint'] as const)
      : policy.upToDate === false
        ? (['warn', 'pending', 'pendingHint'] as const)
        : (['neutral', 'unknown', 'unknownHint'] as const);
  return (
    <p className="console-muted">
      <Badge tone={tone}>{t(`floor.drawer.health.settings.${label}`)}</Badge> {t(`floor.drawer.health.settings.${hint}`)}
    </p>
  );
}

/** Free disk over the window as a polyline, scaled to its own range — the trend, not the numbers. */
function DiskTrend({ samples }: { readonly samples: readonly StationTelemetrySample[] }): ReactNode {
  const { t } = useTranslation('console');
  if (samples.length < 2) return null;
  const values = samples.map((item) => item.diskFreeGb);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const points = values
    .map((value, index) => `${((index / (values.length - 1)) * 100).toFixed(2)},${(36 - ((value - min) / span) * 32).toFixed(2)}`)
    .join(' ');
  return (
    <svg className="console-health__trend" viewBox="0 0 100 40" preserveAspectRatio="none" role="img" aria-label={t('floor.drawer.health.trend')}>
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function StationHealth({ station, canClear = false }: { readonly station: FloorStationState; readonly canClear?: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const venue = useCurrentVenue();
  const health = useStationHealth(venue.organizationId, venue.id, station.stationId, true);
  const telemetry = useStationTelemetry(venue.organizationId, venue.id, station.stationId, true);
  const data = health.data;
  const now = useNow(30_000);
  // The clear form open under the list: one alert's id, `all`, or none.
  const [clearing, setClearing] = useState<string | null>(null);

  let badge: ReactNode = null;
  let body: ReactNode;
  if (health.isError) {
    body = <p className="console-muted">{problem.text(health.error, { fallback: t('floor.drawer.health.failed') })}</p>;
  } else if (data === undefined) {
    body = <p className="console-muted">{t('floor.drawer.health.loading')}</p>;
  } else {
    const critical = data.open.some((alert) => alert.severity === 'critical' && alert.snoozedUntil === undefined);
    const state =
      data.status === 'unhealthy' ? (
        <Badge tone={critical ? 'danger' : 'warn'}>{t('floor.drawer.health.unhealthy')}</Badge>
      ) : data.status === 'healthy' ? (
        <Badge tone="ok">{t('floor.drawer.health.healthy')}</Badge>
      ) : // A PC that has never reported says so in a sentence under the title; a "Not reported" badge beside it would say
      // it twice.
      null;
    badge =
      canClear && data.open.length > 1 ? (
        <>
          {state}
          <Button variant="secondary" size="sm" icon={<Icon name="check" />} onClick={() => setClearing('all')} aria-expanded={clearing === 'all'}>
            {t('floor.drawer.health.clearAll')}
          </Button>
        </>
      ) : (
        state
      );
    const cleared = data.recent.filter((alert) => alert.clearedAt !== null).slice(0, 5);
    // A form for one alert that has since cleared (the PC fixed it, or someone else cleared it) closes on its own.
    const formOpen = canClear && clearing !== null && data.open.length > 0 && (clearing === 'all' || data.open.some((alert) => alert.id === clearing));
    const latest = data.latest;
    body = (
      <>
        {data.status === 'unknown' ? <p className="console-muted">{t('floor.drawer.health.neverReported')}</p> : null}
        <SettingsState policy={data.policy} />
        {data.open.length > 0 ? (
          <ul className="console-health__alerts" aria-label={t('floor.drawer.health.open')}>
            {data.open.map((alert) => (
              <AlertRow key={alert.id} alert={alert} now={now} onClear={canClear ? () => setClearing(alert.id) : undefined} />
            ))}
          </ul>
        ) : null}
        {formOpen && clearing !== null ? (
          <ClearAlertsForm key={clearing} station={station} {...(clearing === 'all' ? {} : { alertIds: [clearing] })} onDone={() => setClearing(null)} />
        ) : null}
        {latest === null ? null : (
          <>
            <dl className="console-health__readings">
              <div>
                <dt>{t('floor.drawer.health.cpu')}</dt>
                <dd className="console-num">{t('floor.drawer.health.percent', { value: latest.cpuPct })}</dd>
              </div>
              <div>
                <dt>{t('floor.drawer.health.ram')}</dt>
                <dd className="console-num">{t('floor.drawer.health.percent', { value: latest.ramPct })}</dd>
              </div>
              <div>
                <dt>{t('floor.drawer.health.disk')}</dt>
                <dd className="console-num">{t('floor.drawer.health.diskValue', { gb: latest.diskFreeGb })}</dd>
              </div>
              <div>
                <dt>{t('floor.drawer.health.gpu')}</dt>
                <dd className="console-num">
                  {latest.gpuTempC === null ? t('floor.drawer.health.gpuUnknown') : t('floor.drawer.health.gpuValue', { temp: latest.gpuTempC })}
                </dd>
              </div>
            </dl>
            <DiskTrend samples={telemetry.data?.samples ?? []} />
            <p className="console-muted">{t('floor.drawer.health.latest', { time: new Date(latest.at).toLocaleString() })}</p>
          </>
        )}
        {cleared.length > 0 ? (
          <ul className="console-health__alerts" aria-label={t('floor.drawer.health.recent')}>
            {cleared.map((alert) => (
              <AlertRow key={alert.id} alert={alert} now={now} />
            ))}
          </ul>
        ) : null}
      </>
    );
  }

  return (
    <Section title={t('floor.drawer.health.title')} titleAs="h3" actions={badge ?? undefined}>
      {body}
    </Section>
  );
}
